const config = {
  defaultAnimation: {
    duration: 800,
  },
  dragSpeed: 0.8,
}

const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    carousel.on('created', carousel =>
      carousel.container.setAttribute(`data-initialised`, true),
    )
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 *  amount: number
 * }} arrows
 */
const useArrows = ({ next, previous, amount = 1 } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    next?.addEventListener('click', () => {
      carousel.moveToIdx(carousel.track.details.abs + amount)
    })
    previous?.addEventListener('click', () => {
      carousel.moveToIdx(carousel.track.details.abs - amount)
    })

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details

      if (next) {
        next.disabled = abs === maxIdx
      }

      if (previous) {
        previous.disabled = abs === min
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
  }
}

/**
 * @param {HTMLElement} indicator
 * @param {template} string
 */
const useCurrentSlideIndicator = (indicator, template = '{{ current }}') => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    if (!indicator) return

    const updateIndicator = () => {
      const { rel, length } = carousel.track.details
      const current = String(
        Math.ceil((rel + 1) / carousel.options.slides.perView),
      )
      const total = String(Math.floor(length + 1))

      if (current === total) return

      indicator.textContent = window.BAO.Utils.translateString(template, {
        current,
        total,
      })
    }

    carousel.on('created', updateIndicator)
    carousel.on('slideChanged', updateIndicator)
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    let timeout
    let mouseOver = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('mouseout', () => {
          mouseOver = false
          setNextTimeout()
        })
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
  }
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
  if (!dots) return
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    const updateDotStates = () => {
      dots.forEach((dot, index) => {
        const isCurrent = carousel.track.details.rel === index

        dot.ariaCurrent = String(isCurrent)
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        carousel.moveToIdx(index)
      })
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

window.BAO.Carousel = {
  config,
  plugins: {
    setInitialised,
    useArrows,
    useAutoplay,
    useCurrentSlideIndicator,
    useDots,
  },
}
